/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// import { RichText, RichTextComponent, Text } from '@msdyn365-commerce/core';
import MsDyn365, { ICookieValue, RichTextComponent, Text } from '@msdyn365-commerce/core';
import * as React from 'react';
import uuid from 'uuid';
import { mFIEmailSubscriptionAsync } from '../../actions/EmailSubscriptionDataActionExtension.g';
import { IMFIEmailSubscription } from '../../actions/EmailSubscriptionDataServiceEntities.g';
import { emailRegex } from '../../Utilities/allRegex';
import { inputGroup, mailingData, MailingListInputGroup } from './components/input-group-mailing-list';
import { IMfrmEmailSubscriptionModalData } from './mfrm-email-subscription-modal.data';
import { IMfrmEmailSubscriptionModalProps } from './mfrm-email-subscription-modal.props.autogenerated';
export interface IMfrmEmailSubscriptionModalViewProps extends IMfrmEmailSubscriptionModalProps<IMfrmEmailSubscriptionModalData> {
    isOpen: boolean;
    isSubscribeSuccessfully: boolean;
    className: string;
    isError: boolean;
    heading: JSX.Element;
    subHeading: JSX.Element;
    sucessMsgHeading: JSX.Element;
    errorMsgHeading: JSX.Element;
    disclosures: JSX.Element;
    successContent: JSX.Element;
    form: JSX.Element;
    successCTA: JSX.Element;
    errorCTA: JSX.Element;
    errorBlock: JSX.Element;
    emailValidationBlock: JSX.Element;
    onCloseModal?(): void;
}

interface IPopupModalState {
    isModalOpen: boolean;
    isError: boolean;
    isSubscribeSuccessfully: boolean;
    // tslint:disable-next-line: no-any
    errorMsg: any;
    isEmailValid: boolean;
    popupReady: boolean;
}

export type popupModalCookie = {
    name: string;
    age: number | undefined;
};

/**
 *
 * MfrmEmailSubscriptionModal component
 * @extends {React.PureComponent<IMfrmEmailSubscriptionModalProps<IMfrmEmailSubscriptionModalData>, IPopupModalState>}
 */
class MfrmEmailSubscriptionModal extends React.Component<IMfrmEmailSubscriptionModalProps<IMfrmEmailSubscriptionModalData>,IPopupModalState> {
    private _cookie: popupModalCookie;
    private _delayStarted: boolean = false;

    constructor(props: IMfrmEmailSubscriptionModalProps<IMfrmEmailSubscriptionModalData>) {
        super(props);
        this._closeModal = this._closeModal.bind(this);
        this.onFinished = this.onFinished.bind(this);
        this.state = {
            isModalOpen: true,
            isError: false,
            isSubscribeSuccessfully: false,
            errorMsg: '',
            isEmailValid: true,
            popupReady: false
        };
        this._cookie = {
            name: 'Mattress-Firm_email_sub',
            age: props.config.remindTime || undefined
        };
    }

    public componentDidMount(): void {
        this.setState({ isModalOpen: true });
    }

    public render(): JSX.Element | null {
        if (!this.props.config.enableModal) {
            return null;
        }
        // we add this._cookieRecievedPreForm because that means *this module did not introduce the cookie*
        // this means we want to notify them that the form was successfully made and not just
        // closing down immediately
        if (this._cookieExist(this._cookie.name)) {
            return null;
        }
        const { isModalOpen, isSubscribeSuccessfully, isError, isEmailValid } = this.state;
        const {
            className,
            heading,
            subHeading,
            disclosures,
            successHeading,
            errorHeading,
            successContent,
            successCTA,
            errorCTA
        } = this.props.config;
        {
            this._startDelay();
        }
        const viewProps = {
            ...this.props,
            ...this.state,
            className: className,
            isOpen: isModalOpen,
            heading: heading && this._renderHeading,
            subHeading: subHeading && this._renderSubHeading,
            sucessMsgHeading: successHeading && this._renderSucessMsgHeading,
            errorMsgHeading: errorHeading && this._renderErrorMsgHeading,
            disclosures: disclosures && this._renderDisclosures,
            successContent: successContent && this._renderSuccessContent,
            successCTA: successCTA && this._renderSuccessCTA,
            errorCTA: errorCTA && this._renderErrorCTA,
            isSubscribed: isSubscribeSuccessfully,
            isError: isError,
            form: this._mailingListJSX,
            errorBlock: this._renderErrorBlock,
            emailValidationBlock: !isEmailValid && this._renderValidationError,
            onCloseModal: this._closeModal
        };
        if (!this.state.popupReady) {
            return null;
        }

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public onFinished = async (dataSet: mailingData) => {
        const { errorContent, showErrorException } = this.props.config;

        if (dataSet.email === '' || !emailRegex(dataSet.email)) {
            this.setState({
                isEmailValid: false,
                isSubscribeSuccessfully: false
            });
        } else {
            const { cookies } = this.props.context.request;
            const zipCode = cookies.get('zip_code').value as string;

            const emailSignup: IMFIEmailSubscription = {
                PersonaSleepStyle: '',
                unique_id: uuid.v4(),
                Email: dataSet.email,
                LeadSource: 'modal',
                PersonaMattressPreference: '',
                Lastname: '',
                LeadDate: new Date().toISOString(),
                Firstname: dataSet.name,
                PostalCode: zipCode ? zipCode : '',
                LeadPage: this.props.context.request.telemetryPageName?.substring(0, 120),
                LeadPlatform: 'D365'
            };
            try {
                const response = await mFIEmailSubscriptionAsync({ callerContext: this.props.context.actionContext }, emailSignup);
                if (response.Status === 'Success') {
                    this.setState({ isSubscribeSuccessfully: true, isEmailValid: true });

                    {
                        /* VSI Customization - START - 44020*/
                    }
                    const pageURL = MsDyn365.isBrowser && window.location.pathname;
                    const email = dataSet.email;
                    const { submitCTA } = this.props.config;
                    // @ts-ignore
                    if(MsDyn365.isBrowser && 'utag' in window){
                        // @ts-ignore
                        // tslint:disable-next-line: object-literal-key-quotes
                        utag.link({
                            event: 'popup-modal-email-signup',
                            event_category: 'email signup tracking',
                            event_action: 'click ' + `${submitCTA}`,
                            event_label: 'from email signup modal: ' + `${pageURL}`,
                            event_noninteraction: 'false',
                            event_type: 'email_capture',
                            customer_email: email
                        });
                    }
                } else {
                    this.setState({
                        isError: true,
                        errorMsg: showErrorException ? response.Message : errorContent,
                        isEmailValid: true
                    });
                }
            } catch (error) {
                this.setState({
                    isError: true,
                    errorMsg: showErrorException ? error : errorContent,
                    isEmailValid: true
                });
            }
        }
    };

    public constructMailingListGroupsFromResources(): { [groupName: string]: inputGroup } {
        const { emailLabel, emailValidation, nameLabel, nameValidation } = this.props.resources;

        return {
            name: {
                labelText: nameLabel,
                validationText: nameValidation,
                placeholder: nameLabel
            },
            email: {
                labelText: emailLabel,
                validationText: emailValidation,
                placeholder: emailLabel
            }
        };
    }

    // Heading for email subscription modal
    private get _renderHeading(): JSX.Element {
        const { heading } = this.props.config;
        return <Text className='modal-email-subscription__title' tag={heading!.tag || 'h2'} text={heading!.text} />;
    }

    // Renders Sub Heading for email subscription modal
    private get _renderSubHeading(): JSX.Element {
        const { subHeading } = this.props.config;
        return <Text className='modal-email-subscription__subtitle' tag={subHeading!.tag || 'h6'} text={subHeading!.text} />;
    }

    // Renders disclosures for email subscription modal
    private get _renderDisclosures(): JSX.Element {
        const { disclosures } = this.props.config;
        return <RichTextComponent className='modal-email-subscription__text' text={disclosures} />;
    }

    // Renders errors occurs while email subscription
    private get _renderValidationError(): JSX.Element {
        return (
            <div className='modal-email-subscription__alert-message'>
                <p className='modal-email-subscription__alert-message--error'>{this.props.resources.emailValidation}</p>
            </div>
        );
    }

    // Renders errors occurs while email subscription
    private get _renderErrorBlock(): JSX.Element {
        const { errorMsg } = this.state;
        return <RichTextComponent className='modal-email-subscription__text' text={errorMsg} />;
    }

    // Heading for Success Message after successfully email subscription
    private get _renderSucessMsgHeading(): JSX.Element {
        const { successHeading } = this.props.config;
        return <Text className='modal-email-subscription__title' tag={successHeading!.tag || 'h2'} text={successHeading!.text} />;
    }
    // Heading for Success Message after successfully email subscription
    private get _renderErrorMsgHeading(): JSX.Element {
        const { errorHeading } = this.props.config;
        return <Text className='modal-email-subscription__title' tag={errorHeading!.tag || 'h2'} text={errorHeading!.text} />;
    }

    // Renders success message content after successfully email subscription
    private get _renderSuccessContent(): JSX.Element {
        const { successContent } = this.props.config;
        return <RichTextComponent className='modal-email-subscription__text' text={successContent} />;
    }

    private get _renderSuccessCTA(): JSX.Element {
        const { successCTA } = this.props.config;
        return (
            <a
                href={successCTA?.linkUrl.destinationUrl}
                aria-label={successCTA?.ariaLabel}
                target={successCTA?.openInNewTab ? '_blank' : undefined}
                className='modal-email-subscription__button'
            >
                {successCTA?.linkText}
            </a>
        );
    }

    private get _renderErrorCTA(): JSX.Element {
        const { errorCTA } = this.props.config;
        return (
            <a
                href={errorCTA?.linkUrl.destinationUrl}
                aria-label={errorCTA?.ariaLabel}
                target={errorCTA?.openInNewTab ? '_blank' : undefined}
                className='modal-email-subscription__button'
            >
                {errorCTA?.linkText}
            </a>
        );
    }

    // Delays the modal to show after specific time span as per delay config
    private _startDelay(): void {
        if (!this._delayStarted) {
            const delayTime = this.props.config.delay || 0;
            this._delayStarted = true;
            MsDyn365.isBrowser &&
                setTimeout(() => {
                    this.setState({ popupReady: true }, () => {
                        // @ts-ignore
                        if ('utag' in window) {
                            const pageURL = MsDyn365.isBrowser && window.location.pathname;
                            // @ts-ignore
                            // tslint:disable-next-line: object-literal-key-quotes
                            utag.link({
                                event: 'popup-modal-loaded',
                                event_category: 'email signup tracking',
                                event_action: 'auto-show-email-signup-modal',
                                event_label: 'from email signup modal: ' + `${pageURL}`,
                                event_noninteraction: 'false'
                            });
                        }
                    });
                }, delayTime * 1000);
        }
    }

    private _setCookie(name: string, deltaDaysFromToday: number): void {
        const { cookies } = this.props.context.request;
        const expirationDate = new Date(Date.now());
        expirationDate.setTime(expirationDate.getTime() + (deltaDaysFromToday * 24 * 60 * 60 * 1000));

        cookies.set(name, false, {
            expires: deltaDaysFromToday ? expirationDate : null,
            isEssential: true
        } as {});
    }

    private _cookieExist(name: string): boolean {
        const { cookies } = this.props.context.request;
        // if cookie exists, don't display
        if (
            // unfortunetly the type does not match the class. isEssential does
            // not exist on cookies.get but is required in this function.
            // override the type to add it
            (cookies.get as (name: string, isEssential: boolean) => ICookieValue<string>)(name, true).status === 'FOUND'
        ) {
            return true;
        }

        return false;
    }

    // method to close modal and reset states
    private _closeModal(): void {
        const remindTime = this.props.config.remindTime || 0;
        this._setCookie(this._cookie.name, remindTime);
        // @ts-ignore
        if (this.state.isSubscribeSuccessfully === false && MsDyn365.isBrowser && 'utag' in window) {
            const pageURL = MsDyn365.isBrowser && window.location.pathname;
            // @ts-ignore
            // tslint:disable-next-line: object-literal-key-quotes
            utag.link({
                event: 'close-email-signup-modal',
                event_category: 'email signup tracking',
                event_action: 'modal close',
                event_label: 'from email signup modal: ' + `${pageURL}`,
                event_noninteraction: 'false'
            });
        }

        this.setState({
            isModalOpen: false,
            isSubscribeSuccessfully: false,
            isError: false,
            errorMsg: '',
            popupReady: false
        });
    }

    private get _mailingListJSX(): JSX.Element {
        const groups = this.constructMailingListGroupsFromResources();
        const { submitCTA } = this.props.config;
        return (
            <form className='modal-email-subscription__form'>
                <MailingListInputGroup
                    submitText={submitCTA}
                    name={groups.name}
                    email={groups.email}
                    onFinish={this.onFinished}
                    context={this.props.context}
                />
            </form>
        );
    }
}

export default MfrmEmailSubscriptionModal;
